import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuthTokenMonitorService } from '../../../core/auth/token-monitor.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-delegated-session-dialog',
  template: `
    <div class="delegated-session-dialog">
      <div class="flex flex-col items-center gap-4 p-4">
        <!-- Icono informativo -->
        <div class="info-icon">
          <i class="pi pi-info-circle" style="font-size: 3rem; color: var(--blue-500);"></i>
        </div>

        <!-- Mensaje principal -->
        <div class="text-center">
          <h3 class="m-0 mb-2 font-bold">Sesión delegada por expirar</h3>
          <p class="m-0 text-600">
            Esta es una <strong>sesión delegada</strong> desde el portal de administración. Por seguridad, la sesión se gestionará automáticamente en:
          </p>
        </div>

        <!-- Contador regresivo -->
        <div class="countdown-timer">
          <span class="time-display">{{ formattedTime }}</span>
        </div>

        <!-- Explicación -->
        <div class="explanation-box">
          <p class="m-0 text-sm text-500">
            <i class="pi pi-info-circle mr-1"></i>
            Las sesiones delegadas no pueden renovarse desde aquí porque no poseen un token de refresco propio. Al expirar, será redirigido al portal de
            administración, donde el administrador podrá generar una nueva sesión delegada si es necesario.
          </p>
        </div>

        <!-- Botón de acción -->
        <div class="flex gap-3 w-full justify-center mt-2">
          <p-button label="Volver al portal de administración" severity="primary" icon="pi pi-arrow-left" (onClick)="onRedirect()" />
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .delegated-session-dialog {
        min-width: 400px;
        max-width: 500px;
      }

      .info-icon {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: var(--blue-100);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: pulse-blue 2s infinite;
      }

      @keyframes pulse-blue {
        0% {
          box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
        }
        70% {
          box-shadow: 0 0 0 15px rgba(59, 130, 246, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
        }
      }

      .countdown-timer {
        background: var(--surface-100);
        border-radius: 8px;
        padding: 1rem 2rem;
        border: 2px solid var(--orange-300);
      }

      .time-display {
        font-size: 2.5rem;
        font-weight: 700;
        font-family: 'Courier New', monospace;
        color: var(--orange-600);
        letter-spacing: 0.1em;
      }

      .explanation-box {
        background: var(--blue-50);
        border: 1px solid var(--blue-200);
        border-radius: 8px;
        padding: 0.75rem 1rem;
        max-width: 400px;
      }

      :host ::ng-deep .p-button {
        min-width: 200px;
      }
    `,
  ],
  standalone: false,
})
export class DelegatedSessionDialogComponent implements OnInit, OnDestroy {
  formattedTime: string = '00:00';
  private subscription?: Subscription;

  constructor(
    private ref: DynamicDialogRef,
    private tokenMonitor: AuthTokenMonitorService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.updateTime();
    this.subscription = new Subscription();

    const interval = setInterval(() => {
      this.updateTime();

      // Si el tiempo llegó a 0, cerrar diálogo (el monitor redirige)
      if (this.tokenMonitor.timeRemaining() <= 0) {
        clearInterval(interval);
        this.ref.close({ action: 'expired' });
      }
    }, 500);

    this.subscription.add(() => clearInterval(interval));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private updateTime(): void {
    this.formattedTime = this.tokenMonitor.formatTimeRemaining();
  }

  onRedirect(): void {
    this.ref.close({ action: 'redirect' });
    this.tokenMonitor.declineExtension();
  }
}
